/**
 * TanStack Query Hooks for Table Operations
 *
 * Leverages TanStack Query's capabilities for:
 * - Automatic caching and deduplication
 * - Background refetching
 * - Optimistic updates
 * - Request cancellation
 * - Error handling and retry logic
 * - Query invalidation
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { TableOperation, TableOperationType, TableStatus, PaginatedOperationsResponse, DiffChangeType } from '@loader/shared-types';

import { useRpc, extractErrorMessage, parseErrorBody } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateVersionQueries } from '@/lib/queryInvalidation';
import type { Table } from '@/lib/types';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Invalidate all schema-related queries for a table.
 * This is the standard set of invalidations that should happen after any schema change.
 * Exported for use in components that need to manually invalidate after bulk operations.
 */
export function invalidateSchemaQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  tableId: string
): void {
  // Core table data
  void queryClient.invalidateQueries({ queryKey: queryKeys.tables.fields(tableId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.tables.detail(tableId) });
  // Operations journal
  void queryClient.invalidateQueries({ queryKey: queryKeys.tables.operations.all(tableId) });
  // Data preview (uses custom query key prefix)
  void queryClient.invalidateQueries({ queryKey: [`table-data-${tableId}`] });
  // Linked tables that may reference this table's fields
  void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });
}

// ============================================================================
// LIST QUERIES
// ============================================================================

/**
 * Hook to fetch list of tables
 *
 * Features:
 * - Automatic caching (30s stale time)
 * - Optional filtering by status
 * - Pagination support
 */
export function useTablesListQuery(options?: {
  limit?: number;
  status?: 'active' | 'draft' | 'disabled';
  enabled?: boolean;
}) {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.tables.list({ limit: options?.limit, status: options?.status }),
    queryFn: async () => {
      const query: Record<string, string> = {};
      if (options?.limit) query.limit = String(options.limit);
      if (options?.status) query.status = options.status;

      const res = await rpc.api.private.tables.$get({ query });

      if (!res.ok) {
        throw new Error('Failed to fetch tables');
      }

      const response = await res.json();
      return response.data as Table[];
    },
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch all tables in the workspace
 *
 * Convenience alias for useTablesListQuery without filters.
 * Matches the naming convention of useFoldersQuery.
 *
 * Features:
 * - Automatic caching (30s stale time)
 * - Background refetching
 */
export function useTablesQuery(options?: { enabled?: boolean }) {
  return useTablesListQuery(options);
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Hook to update a table
 *
 * Features:
 * - Automatic cache invalidation
 * - Error handling
 */
export function useUpdateTableMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Table> }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].$put({
        param: { id },
        json: data,
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to update table'));
      }

      return res.json();
    },
    onSuccess: (_, variables) => {
      // Invalidate related queries
      void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tables.detail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataGrid.tables });
    },
  });
}

/**
 * Hook to update table status
 *
 * Features:
 * - Automatic cache invalidation
 * - Error handling
 */
export function useUpdateTableStatusMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TableStatus }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].$put({
        param: { id },
        json: { status },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to update table status'));
      }

      return res.json();
    },
    onSuccess: (_, variables) => {
      // Invalidate related queries
      void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tables.detail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataGrid.tables });
    },
  });
}

/**
 * Hook to delete a table
 *
 * Features:
 * - Automatic cache invalidation
 * - Error handling with dependency detection
 */
export function useDeleteTableMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].$delete({
        param: { id },
      });

      if (!res.ok) {
        // Parse error response - try text first, then parse as JSON
        const text = await res.text();
        const error = parseErrorBody(text, 'Failed to delete table');

        // Attach status for dependency detection
        const errMessage = (error.error as string) || (error.message as string) || 'Failed to delete table';
        const err = new Error(errMessage);
        (err as Error & { status?: number; dependentViews?: string[] }).status = res.status;
        if (error.dependentViews) {
          (err as Error & { dependentViews?: string[] }).dependentViews = error.dependentViews as string[];
        }
        throw err;
      }

      return res.json();
    },
    onSuccess: () => {
      // Invalidate related queries
      void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataGrid.tables });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataGrid.linkedTables });
      // Table deletion cascades to connectors — refresh Inputs page
      void queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
    },
  });
}

/**
 * Hook to fetch a single table by ID
 * 
 * Features:
 * - Automatic caching (30s stale time)
 * - Automatic refetching on mount if stale
 * - Error handling with retry logic
 */
export function useTableQuery(id: string | undefined) {
  const rpc = useRpc();

  return useQuery({
    queryKey: id ? queryKeys.tables.detail(id) : queryKeys.tables.detailDisabled,
    queryFn: async () => {
      if (!id) throw new Error('Table ID is required');
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].$get({
        param: { id },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch table');
      }

      const data = await res.json();
      return data.table;
    },
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds - table metadata doesn't change often
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch table versions
 * 
 * Features:
 * - Caching with automatic invalidation when versions change
 * - Background refetching
 * - Normalized data transformation
 */
export function useTableVersionsQuery(
  tableId: string | undefined,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) {
  const rpc = useRpc();

  return useQuery({
    queryKey: tableId
      ? queryKeys.tables.versions(tableId)
      : queryKeys.tables.versionsDisabled,
    queryFn: async () => {
      if (!tableId) throw new Error('Table ID is required');
      const res = await rpc.api.private.versions.$get({
        query: { table_id: tableId },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch versions');
      }

      const response = await res.json();

      // RPC returns data directly without wrapper
      const versionsArray = response.data || [];

      // Return API data with snake_case fields - no transformations
      // Includes all fields from API response (superset of VersionOption)
      interface VersionApiItem {
        id: string;
        version_number?: number;
        file_name?: string;
        file_size?: number;
        parquet_size?: number;
        status: string;
        created_at: string;
        created_by?: string;
        table_id?: string;
        records_processed?: number;
        error_message?: string | null;
        error_details?: Record<string, unknown> | null;
        system_tag?: 'current' | 'previous' | null;
        /** Link to the operation that processed this version */
        operation_id?: string | null;
        tags?: Array<{ id: string; name: string; color: string | null }>;
      }
      return (versionsArray as VersionApiItem[]).map((v) => ({
        id: v.id,
        file_name: v.file_name ?? 'unknown',
        version_number: v.version_number,
        status: v.status,
        system_tag: v.system_tag ?? null,
        operation_id: v.operation_id ?? null,
        tags: v.tags ?? [],
        created_at: v.created_at,
        // Additional fields from API (not in VersionOption but used by other components)
        file_size: v.file_size ?? 0,
        parquet_size: v.parquet_size ?? 0,
        created_by: v.created_by,
        table_id: v.table_id,
        records_processed: v.records_processed ?? 0,
        error_message: v.error_message,
        error_details: v.error_details,
      }));
    },
    enabled: !!tableId && (options?.enabled !== false),
    staleTime: 10 * 1000, // 10 seconds - versions can change frequently
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook to fetch diff data between two versions with pagination, filtering, and sorting
 *
 * Uses the unified query endpoint which executes the diff via DuckDB API
 * and returns the results directly (not token-based like table/linkedTable queries).
 *
 * Features:
 * - Automatic caching per version pair and pagination state
 * - Error handling with retry logic
 * - Server-side pagination, filtering by change type, and sorting
 */
export function useVersionDiffQuery(
  tableId: string | undefined,
  fromId: string | null | undefined,
  toId: string | null | undefined,
  options?: {
    enabled?: boolean;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    changeType?: DiffChangeType;
  }
) {
  const rpc = useRpc();

  // Include pagination/filter/sort params in query key for proper caching
  const diffQueryKey = tableId && fromId && toId
    ? [
        ...queryKeys.tables.diff(tableId, fromId, toId),
        options?.limit,
        options?.offset,
        options?.sortBy,
        options?.sortDir,
        options?.changeType,
      ]
    : queryKeys.tables.diffDisabled;

  return useQuery({
    queryKey: diffQueryKey,
    queryFn: async () => {
      if (!tableId || !fromId || !toId) {
        throw new Error('Table ID, From ID, and To ID are required');
      }

      // Use the unified query endpoint for diff queries
      // Unlike table/linkedTable queries, diff returns data directly (not a token)
      const res = await rpc.api.private.query.$post({
        json: {
          diff: {
            tableId: tableId,
            left: fromId,
            right: toId,
            change_type: options?.changeType,
          },
          format: 'json',
          limit: options?.limit,
          offset: options?.offset,
        },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to fetch diff'));
      }

      // Diff endpoint returns data directly (executed server-side via DuckDB API)
      const response = await res.json() as {
        data: Array<Record<string, unknown>>;
        counts: {
          added: number;
          removed: number;
          unchanged: number;
        };
        pagination: {
          total: number;
          limit: number;
          offset: number;
        };
        meta?: {
          leftVersion: { id: string; version_number: number; file_name: string };
          rightVersion: { id: string; version_number: number; file_name: string };
        };
      };

      // Return paginated data format matching ChangesView.diffData type
      const limit = response.pagination?.limit || options?.limit || 100;
      const offset = response.pagination?.offset || options?.offset || 0;
      const total = response.pagination?.total || 0;
      const page = Math.floor(offset / limit) + 1;
      const pages = Math.ceil(total / limit) || 1;

      return {
        data: response.data || [],
        counts: response.counts || { added: 0, removed: 0, unchanged: 0 },
        pagination: {
          limit,
          offset,
          total,
          page,
          pages,
        },
      };
    },
    enabled:
      !!tableId &&
      !!fromId &&
      !!toId &&
      (options?.enabled !== false),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    // Don't retry on 4xx/5xx errors
    retry: (failureCount, error) => {
      if (error instanceof Error && (error.message.includes('4') || error.message.includes('5'))) {
        return false;
      }
      return failureCount < 1;
    },
  });
}

/**
 * Hook to update version system tag (current/previous)
 * Uses mutation with optimistic updates
 *
 * Features:
 * - Optimistic UI updates (instant feedback)
 * - Automatic rotation (setting 'current' clears old 'current')
 * - Error rollback to previous state
 */
export function useUpdateVersionSystemTagMutation(tableId: string | undefined) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      versionId,
      systemTag,
    }: {
      versionId: string;
      systemTag: 'current' | 'previous' | null;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.versions[':id']['system-tag'].$patch({
        param: { id: versionId },
        json: { system_tag: systemTag },
      });

      if (!res.ok) {
        throw new Error('Failed to update version system tag');
      }

      const response = await res.json();
      return response;
    },
    onMutate: async ({ versionId, systemTag }) => {
      if (!tableId) return;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.tables.versions(tableId) });

      // Snapshot previous value
      const previousVersions = queryClient.getQueryData(queryKeys.tables.versions(tableId));

      // Optimistically update cache
      queryClient.setQueryData(
        queryKeys.tables.versions(tableId),
        (old: Array<{ id: string; system_tag: 'current' | 'previous' | null }> | undefined) => {
          if (!old) return old;
          return old.map((v) => {
            // Clear old tag of same type (rotation)
            if (systemTag && v.system_tag === systemTag && v.id !== versionId) {
              return { ...v, system_tag: null };
            }
            // Update target version
            if (v.id === versionId) {
              return { ...v, system_tag: systemTag };
            }
            return v;
          });
        }
      );

      return { previousVersions };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (tableId && context?.previousVersions) {
        queryClient.setQueryData(queryKeys.tables.versions(tableId), context.previousVersions);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      if (tableId) {
        void invalidateVersionQueries(queryClient, tableId);
        void queryClient.invalidateQueries({
          queryKey: queryKeys.tables.detail(tableId),
        });
      }
    },
  });
}

// ============================================================================
// TABLE OPERATIONS (Type Conversions, etc.)
// ============================================================================

/**
 * Hook to fetch the active operation for a table (if any)
 *
 * Used to show operation status banners and block certain UI actions.
 *
 * Features:
 * - Short polling (refetch every 5s when operation is active)
 * - Automatic caching
 */
export function useTableActiveOperationQuery(tableId: string | undefined) {
  const rpc = useRpc();

  // Track when polling started to implement timeout
  const pollingStartTimeRef = React.useRef<number | null>(null);
  const POLLING_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

  return useQuery({
    queryKey: tableId
      ? queryKeys.tables.operations.active(tableId)
      : queryKeys.tables.operations.activeDisabled,
    queryFn: async () => {
      if (!tableId) throw new Error('Table ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].operations.active.$get({
        param: { id: tableId },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch active operation');
      }

      const response = (await res.json()) as { operation: TableOperation | null };
      return response.operation;
    },
    enabled: !!tableId,
    staleTime: 5 * 1000, // 5 seconds - operations change state frequently
    gcTime: 30 * 1000, // 30 seconds
    refetchInterval: (query) => {
      const operation = query.state.data;

      // If no active operation, stop polling and reset timer
      if (!operation || (operation.status !== 'pending' && operation.status !== 'in_progress')) {
        pollingStartTimeRef.current = null;
        return false;
      }

      // Initialize polling start time on first active operation
      if (pollingStartTimeRef.current === null) {
        pollingStartTimeRef.current = Date.now();
      }

      // Check if polling timeout has been reached
      const pollingDuration = Date.now() - pollingStartTimeRef.current;
      if (pollingDuration > POLLING_TIMEOUT_MS) {
        pollingStartTimeRef.current = null;
        // Polling timeout reached - stop polling silently
        return false;
      }

      // Continue polling every 5 seconds
      return 5000;
    },
  });
}

/**
 * Hook to fetch operations for a table with server-side pagination
 *
 * Use this for the operations list view with pagination.
 * For timeline/preview display, use useTableRecentOperationsQuery instead.
 *
 * Features:
 * - Server-side pagination (offset/limit)
 * - Filtering by type and status
 * - Automatic caching
 */
export function useTableOperationsListQuery(
  tableId: string | undefined,
  options?: {
    type?: TableOperationType;
    status?: 'pending' | 'in_progress' | 'completed' | 'failed';
    limit?: number;
    offset?: number;
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) {
  const rpc = useRpc();

  return useQuery({
    queryKey: tableId
      ? queryKeys.tables.operations.list(tableId, {
          type: options?.type,
          status: options?.status,
          limit: options?.limit,
          offset: options?.offset,
        })
      : queryKeys.tables.operations.recentDisabled,
    queryFn: async () => {
      if (!tableId) throw new Error('Table ID is required');

      const query: Record<string, string> = {};
      if (options?.type) query.type = options.type;
      if (options?.status) query.status = options.status;
      if (options?.limit) query.limit = String(options.limit);
      if (options?.offset) query.offset = String(options.offset);

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].operations.$get({
        param: { id: tableId },
        query: Object.keys(query).length > 0 ? query : undefined,
      });

      if (!res.ok) {
        throw new Error('Failed to fetch operations');
      }

      const response = (await res.json()) as PaginatedOperationsResponse;
      return response;
    },
    enabled: !!tableId && (options?.enabled !== false),
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook to fetch recent operations for a table (timeline display)
 *
 * Uses the paginated /operations endpoint with a small limit.
 * For full pagination support, use useTableOperationsListQuery instead.
 *
 * Features:
 * - Automatic caching
 * - Simplified return (just operations array)
 */
export function useTableRecentOperationsQuery(
  tableId: string | undefined,
  options?: { limit?: number; enabled?: boolean; refetchInterval?: number | false }
) {
  const rpc = useRpc();
  const limit = options?.limit ?? 10;

  return useQuery({
    queryKey: tableId
      ? queryKeys.tables.operations.recent(tableId)
      : queryKeys.tables.operations.recentDisabled,
    queryFn: async () => {
      if (!tableId) throw new Error('Table ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].operations.$get({
        param: { id: tableId },
        query: { limit: String(limit) },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch recent operations');
      }

      const response = (await res.json()) as { operations: TableOperation[]; total: number };
      return response.operations;
    },
    enabled: !!tableId && (options?.enabled !== false),
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook to fetch a specific operation by ID
 */
export function useTableOperationQuery(tableId: string | undefined, operationId: string | undefined) {
  const rpc = useRpc();

  return useQuery({
    queryKey:
      tableId && operationId
        ? queryKeys.tables.operations.detail(tableId, operationId)
        : queryKeys.tables.operations.detailDisabled,
    queryFn: async () => {
      if (!tableId || !operationId) throw new Error('Table ID and Operation ID are required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].operations[':operationId'].$get({
        param: { id: tableId, operationId },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch operation');
      }

      const response = (await res.json()) as { operation: TableOperation };
      return response.operation;
    },
    enabled: !!tableId && !!operationId,
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch all events for a specific operation
 *
 * Returns all events including validation errors.
 */
export function useOperationEventsQuery(
  tableId: string | undefined,
  operationId: string | undefined,
  options?: { enabled?: boolean; limit?: number }
) {
  const rpc = useRpc();

  return useQuery({
    queryKey:
      tableId && operationId
        ? queryKeys.tables.operations.events(tableId, operationId)
        : queryKeys.tables.operations.detailDisabled,
    queryFn: async () => {
      if (!tableId || !operationId) throw new Error('Table ID and Operation ID are required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].operations[':operationId'].events.$get({
        param: { id: tableId, operationId },
        query: { limit: String(options?.limit ?? 100) },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch operation events');
      }

      const response = (await res.json()) as {
        events: import('@loader/shared-types').OperationEvent[];
        pagination: { page: number; limit: number; total: number; pages: number };
      };
      return response;
    },
    enabled: (options?.enabled ?? true) && !!tableId && !!operationId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to initiate a field type change operation
 *
 * Creates an async operation that rewrites Parquet files via DuckDB API.
 */
export function useChangeFieldTypeMutation(tableId: string | undefined) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fieldId, toType }: { fieldId: string; toType: string }) => {
      if (!tableId) throw new Error('Table ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].fields[':fieldId']['change-type'].$post({
        param: { id: tableId, fieldId },
        json: { to_type: toType },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to initiate type conversion'));
      }

      const response = (await res.json()) as {
        operation: {
          id: string;
          status: string;
          type: TableOperationType;
          field_name: string;
          from_type: string;
          to_type: string;
          version_count: number;
        };
      };

      return response.operation;
    },
    onSuccess: () => {
      if (tableId) {
        invalidateSchemaQueries(queryClient, tableId);
      }
    },
  });
}

/**
 * Hook to initiate a batch field type change operation
 *
 * All fields are validated in a preflight phase. If ANY validation fails,
 * the entire operation is rejected (atomic all-or-nothing).
 */
export function useBatchChangeFieldTypeMutation(tableId: string | undefined) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fieldConversions: Array<{ fieldId: string; toType: string }>) => {
      if (!tableId) throw new Error('Table ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].fields['batch-change-type'].$post({
        param: { id: tableId },
        json: {
          field_conversions: fieldConversions.map((fc) => ({
            field_id: fc.fieldId,
            to_type: fc.toType,
          })),
        },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to initiate batch type conversion'));
      }

      const response = (await res.json()) as {
        operation: {
          id: string;
          status: string;
          type: TableOperationType;
          version_count: number;
          field_count: number;
          field_conversions: Array<{
            field_id: string;
            field_name: string;
            from_type: string;
            to_type: string;
          }>;
        };
      };

      return response.operation;
    },
    onSuccess: () => {
      if (tableId) {
        invalidateSchemaQueries(queryClient, tableId);
      }
    },
  });
}

/**
 * Hook to initiate a field constraint change operation
 *
 * For tightening constraints (false → true): Creates async validation operation
 * For relaxing constraints (true → false): Applied immediately
 */
export function useChangeFieldConstraintMutation(tableId: string | undefined) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fieldId,
      constraintType,
      toValue,
    }: {
      fieldId: string;
      constraintType: 'unique' | 'required';
      toValue: boolean;
    }) => {
      if (!tableId) throw new Error('Table ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].fields[':fieldId']['change-constraint'].$post({
        param: { id: tableId, fieldId },
        json: { constraint_type: constraintType, to_value: toValue },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to initiate constraint change'));
      }

      const response = (await res.json()) as {
        operation: {
          id: string;
          status: string;
          type: TableOperationType;
          field_name: string;
          constraint_type: 'unique' | 'required';
          from_value: boolean;
          to_value: boolean;
          version_count: number;
          message?: string;
        };
      };

      return response.operation;
    },
    onSuccess: () => {
      if (tableId) {
        invalidateSchemaQueries(queryClient, tableId);
      }
    },
  });
}

/**
 * Hook to cancel a pending operation
 */
export function useCancelOperationMutation(tableId: string | undefined) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (operationId: string) => {
      if (!tableId) throw new Error('Table ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].operations[':operationId'].$delete({
        param: { id: tableId, operationId },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to cancel operation'));
      }

      return res.json();
    },
    onSuccess: () => {
      if (tableId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.tables.operations.all(tableId),
        });
      }
    },
  });
}

/**
 * Hook to update field properties (e.g., description, is_enabled)
 *
 * This is for immediate updates that don't require async operations.
 * For type changes use useBatchChangeFieldTypeMutation.
 * For constraint changes use useChangeFieldConstraintMutation.
 */
export function useUpdateFieldMutation(tableId: string | undefined) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: { fieldId: string; description?: string | null; is_enabled?: boolean }) => {
      if (!tableId) throw new Error('Table ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].fields.$put({
        param: { id: tableId },
        json: {
          fields: [{
            id: updates.fieldId,
            description: updates.description,
            is_enabled: updates.is_enabled,
          }],
        },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to update field'));
      }

      const response = (await res.json()) as { fields: Array<{ id: string; description: string | null; is_enabled: boolean }> };
      return response.fields[0];
    },
    onSuccess: () => {
      if (tableId) {
        invalidateSchemaQueries(queryClient, tableId);
      }
    },
  });
}

/**
 * Hook to update field constraints via PUT /fields (for draft tables)
 *
 * This is used for draft tables where constraint changes don't require validation.
 * For finalized tables, use useChangeFieldConstraintMutation instead.
 */
export function useUpdateFieldConstraintsMutation(tableId: string | undefined) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Array<{ fieldId: string; is_required?: boolean; is_unique?: boolean }>) => {
      if (!tableId) throw new Error('Table ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].fields.$put({
        param: { id: tableId },
        json: {
          fields: updates.map((u) => ({
            id: u.fieldId,
            is_required: u.is_required,
            is_unique: u.is_unique,
          })),
        },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to update field constraints'));
      }

      const response = (await res.json()) as { fields: Array<{ id: string; is_required: boolean; is_unique: boolean }> };
      return response.fields;
    },
    onSuccess: () => {
      if (tableId) {
        invalidateSchemaQueries(queryClient, tableId);
      }
    },
  });
}

/**
 * Hook to reorder fields in a table
 *
 * Updates the order_index of multiple fields at once.
 * Provides optimistic updates and proper cache invalidation.
 */
export function useReorderFieldsMutation(tableId: string | undefined) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fieldIds: string[]) => {
      if (!tableId) throw new Error('Table ID is required');

      // Build fields array with new order_index values
      const fields = fieldIds.map((id, index) => ({
        id,
        order_index: index,
      }));

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].fields.$put({
        param: { id: tableId },
        json: { fields },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to reorder fields'));
      }

      const response = (await res.json()) as { fields: Array<{ id: string; order_index: number }> };
      return response.fields;
    },
    onMutate: async (fieldIds: string[]) => {
      if (!tableId) return;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.tables.detail(tableId) });

      // Snapshot previous value
      const previousDetail = queryClient.getQueryData(queryKeys.tables.detail(tableId));

      // Optimistically update cache - reorder fields based on fieldIds array
      queryClient.setQueryData(
        queryKeys.tables.detail(tableId),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (old: any) => {
          if (!old?.fields) return old;
          // Create a map for O(1) lookup
          const fieldMap = new Map(old.fields.map((f: { id: string }) => [f.id, f]));
          // Reorder fields based on fieldIds and update order_index
          const reorderedFields = fieldIds
            .map((id, index) => {
              const field = fieldMap.get(id);
              if (!field) return null;
              return { ...field, order_index: index };
            })
            .filter(Boolean);
          return { ...old, fields: reorderedFields };
        }
      );

      return { previousDetail };
    },
    onError: (_err, _fieldIds, context) => {
      // Roll back on error
      if (tableId && context?.previousDetail) {
        queryClient.setQueryData(queryKeys.tables.detail(tableId), context.previousDetail);
      }
    },
    onSuccess: () => {
      if (tableId) {
        invalidateSchemaQueries(queryClient, tableId);
      }
    },
  });
}

/**
 * Hook to create a new field for a table
 *
 * Features:
 * - Automatic cache invalidation
 * - Error handling
 */
export function useCreateFieldMutation(tableId: string | undefined) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      data_type: string;
      is_required?: boolean;
      is_unique?: boolean;
      description?: string;
      order_index?: number;
    }) => {
      if (!tableId) throw new Error('Table ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].fields.$post({
        param: { id: tableId },
        json: data,
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to create field'));
      }

      const response = (await res.json()) as {
        field: {
          id: string;
          name: string;
          data_type: string;
          is_required: boolean;
          is_unique: boolean;
          description: string | null;
          order_index: number;
        };
        warnings?: string[];
      };

      return response;
    },
    onSuccess: () => {
      if (tableId) {
        invalidateSchemaQueries(queryClient, tableId);
      }
    },
  });
}

/**
 * Mutation hook for deleting a version
 * Provides optimistic updates and proper cache invalidation
 */
export function useDeleteVersionMutation(tableId: string | undefined) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (versionId: string) => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.versions[':id'].$delete({
        param: { id: versionId },
      });

      if (!res.ok) {
        throw new Error('Failed to delete version');
      }

      return { versionId };
    },
    onMutate: async (versionId) => {
      if (!tableId) return;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.tables.versions(tableId) });

      // Snapshot previous value
      const previousVersions = queryClient.getQueryData(queryKeys.tables.versions(tableId));

      // Optimistically remove from cache
      queryClient.setQueryData(
        queryKeys.tables.versions(tableId),
        (old: Array<{ id: string }> | undefined) => {
          if (!old) return old;
          return old.filter((v) => v.id !== versionId);
        }
      );

      return { previousVersions };
    },
    onError: (_err, _versionId, context) => {
      // Rollback on error
      if (tableId && context?.previousVersions) {
        queryClient.setQueryData(queryKeys.tables.versions(tableId), context.previousVersions);
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      if (tableId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.tables.versions(tableId) });
      }
    },
  });
}

